import { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { SecretSantaGame } from "@/api/types.ts";
import { getSecretSantaGame } from "@/api/apiMethods.ts";
import { useParams } from "react-router-dom";

interface SecretSantaContextType {
  game: SecretSantaGame | null;
  setGame: (game: SecretSantaGame | null) => void;
  refreshGame: () => Promise<void>;
  isLoading: boolean;
}

const SecretSantaContext = createContext<SecretSantaContextType | undefined>(undefined);

export const SecretSantaProvider = ({ children }: { children: ReactNode }) => {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<SecretSantaGame | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshGame = async () => {
    if (!gameId) return;
    
    try {
      setIsLoading(true);
      const fetchedGame = await getSecretSantaGame(gameId);
      setGame(fetchedGame);
    } catch (error) {
      console.error('Failed to fetch game:', error);
      setGame(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshGame();
  }, [gameId]);

  return (
    <SecretSantaContext.Provider value={{ game, setGame, refreshGame, isLoading }}>
      {children}
    </SecretSantaContext.Provider>
  );
};

export const useSecretSanta = (): SecretSantaContextType => {
  const context = useContext(SecretSantaContext);

  if (context === undefined) {
    throw new Error('useSecretSanta must be used within a SecretSantaProvider');
  }

  return context;
};
